import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Card, Col, Container, Row, Alert, Button, Spinner } from "reactstrap";

import AuthSlider from "./AuthSlider";
import { APP_NAME_SHORT } from "common/constants";
import { postReactivateAccount } from "helpers/backend_helper";

const ReactivateAccount = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const [loading, setLoading] = useState<boolean>(false);
  const [successMsg, setSuccessMsg] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    document.title = "Reactivate Account | CIMS";
  }, []);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setErrorMsg("Missing reactivation token.");
      return;
    }

    const reactivate = async () => {
      try {
        setLoading(true);
        await postReactivateAccount({ token });
        setSuccessMsg(
          "Your account has been reactivated. You can now sign in."
        );
      } catch (e: any) {
        const message =
          e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          "Failed to reactivate account.";
        setErrorMsg(message);
      } finally {
        setLoading(false);
      }
    };

    reactivate();
  }, [searchParams]);

  return (
    <React.Fragment>
      <div
        className="auth-page-wrapper auth-bg-cover py-5 d-flex justify-content-center align-items-center min-vh-100"
        style={{
          background: "rgba(1,78,107, 0.9)",
        }}
      >
        <div className="bg-overlay"></div>
        <div className="auth-page-content overflow-hidden pt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <Card className="overflow-hidden card-bg-fill galaxy-border-none">
                  <Row className="g-0">
                    <AuthSlider />

                    <Col lg={6}>
                      <div className="p-lg-5 p-4">
                        <div>
                          <h5 className="text-primary">Account Reactivation</h5>
                          <p className="text-muted">
                            Processing your request for {APP_NAME_SHORT}
                          </p>
                        </div>

                        {errorMsg ? (
                          <Alert color="danger">{errorMsg}</Alert>
                        ) : null}
                        {successMsg ? (
                          <Alert color="success">{successMsg}</Alert>
                        ) : null}

                        <div className="mt-4">
                          <Button
                            color="primary"
                            className="w-100"
                            disabled={loading}
                            onClick={() => navigate("/login")}
                          >
                            {loading ? (
                              <Spinner size="sm" className="me-2">
                                Loading...
                              </Spinner>
                            ) : null}
                            Go to Sign In
                          </Button>
                        </div>

                        <div className="mt-5 text-center">
                          <p className="mb-0">
                            Need a new link? Contact your administrator or try
                            <Link
                              to="/forgot-password"
                              className="fw-semibold text-primary text-decoration-underline ms-1"
                            >
                              password reset
                            </Link>
                            .
                          </p>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>

        <footer className="footer galaxy-border-none">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center">
                  <p className="mb-0">
                    CIMS {new Date().getFullYear()} - Risk Management Unit
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>
    </React.Fragment>
  );
};

export default ReactivateAccount;
